// ABOUTME: Renders owner-only project, agent-profile, and workspace-policy administration.
// ABOUTME: Sensitive policy changes obtain an action-bound passkey proof before mutation.

import { useCallback, useEffect, useState } from "react";

import { requestStepUpProof } from "./auth/webauthn.js";

type Provider = "claude" | "codex" | "grok";

interface ProjectRecord {
  id: string;
  name: string;
  slug: string;
  tint: string;
  access_mode: "workspace" | "restricted";
  repository_host: string;
  hosted_repository_id: string;
  repository_subpath: string;
  resource_version: number;
}

interface AgentProfileRecord {
  id: string;
  name: string;
  provider: Provider;
  model: string | null;
  execution_mode: "interactive" | "headless";
  harness_mode: "restricted" | "standard";
  permission_mode: "manual" | "autonomous";
  resource_version: number;
}

interface WorkspacePolicy {
  allowedProviders: string[];
  allowAgentRootPropose: boolean;
  allowPassToAgent: boolean;
  allowRunOverrides: boolean;
  offlineAgentWork: { allowed_tools: string[]; max_pending_age_seconds: number };
  offlineAgentResults: { allow_submit_result: boolean; max_pending_age_seconds: number };
  resourceVersion: number;
}

const KNOWN_PROVIDERS = ["claude", "codex", "grok"] as const;
const OFFLINE_AGENT_TOOLS = [
  ["bfb_add_comment", "Comments"],
  ["bfb_propose_task", "Task proposals"],
  ["bfb_report_progress", "Progress reports"],
  ["bfb_update_task", "Task title and summary edits"],
] as const;

export interface WorkspaceSettingsProps {
  workspaceId: string;
  authorizationEpoch: number;
  csrfToken: string;
  fetchImpl: typeof fetch;
  onChanged: () => void;
}

function requestId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

async function sha256Json(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${[...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")}`;
}

export async function workspacePolicyStepUpTarget(
  workspaceId: string,
  expectedVersion: number,
  settings: {
    allowed_providers: string[];
    allow_agent_root_propose: boolean;
    allow_pass_to_agent: boolean;
    allow_run_overrides: boolean;
    offline_agent_work: WorkspacePolicy["offlineAgentWork"];
    offline_agent_results: WorkspacePolicy["offlineAgentResults"];
  },
): Promise<string> {
  return sha256Json([
    "BFB-POLICY-UPDATE-V3",
    "workspace.policy.update",
    workspaceId,
    null,
    expectedVersion,
    [...new Set(settings.allowed_providers)].sort(),
    settings.allow_agent_root_propose,
    settings.allow_pass_to_agent,
    settings.allow_run_overrides,
    [...new Set(settings.offline_agent_work.allowed_tools)].sort(),
    settings.offline_agent_work.max_pending_age_seconds,
    settings.offline_agent_results.allow_submit_result,
    settings.offline_agent_results.max_pending_age_seconds,
  ]);
}

export async function responseError(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === "object") {
      if ("message" in body && typeof body.message === "string") return body.message;
      if ("error" in body) {
        if (typeof body.error === "string") return body.error;
        if (
          body.error &&
          typeof body.error === "object" &&
          "message" in body.error &&
          typeof body.error.message === "string"
        )
          return body.error.message;
      }
    }
  } catch {}
  return `Request failed (${response.status})`;
}

export function WorkspaceSettings(props: WorkspaceSettingsProps) {
  const fetchFn = props.fetchImpl;
  const [projects, setProjects] = useState<ProjectRecord[]>([]);
  const [profiles, setProfiles] = useState<AgentProfileRecord[]>([]);
  const [policy, setPolicy] = useState<WorkspacePolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const providerOptions: string[] = policy
    ? [...new Set([...KNOWN_PROVIDERS, ...policy.allowedProviders])]
    : [...KNOWN_PROVIDERS];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const base = `/api/v1/workspaces/${props.workspaceId}`;
      const [projectResponse, profileResponse, policyResponse] = await Promise.all([
        fetchFn(`${base}/projects?limit=100`),
        fetchFn(`${base}/agent-profiles?limit=100`),
        fetchFn(`${base}/workspace-policy`),
      ]);
      if (!projectResponse.ok || !profileResponse.ok || !policyResponse.ok) {
        const failed = [projectResponse, profileResponse, policyResponse].find(
          (response) => !response.ok,
        )!;
        throw new Error(await responseError(failed));
      }
      const projectBody = (await projectResponse.json()) as { projects: ProjectRecord[] };
      const profileBody = (await profileResponse.json()) as { profiles: AgentProfileRecord[] };
      const policyBody = (await policyResponse.json()) as { policy: WorkspacePolicy };
      setProjects(projectBody.projects);
      setProfiles(profileBody.profiles);
      setPolicy(policyBody.policy);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Workspace controls failed to load.");
    } finally {
      setLoading(false);
    }
  }, [fetchFn, props.workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function mutate(action: () => Promise<Response>, success: string): Promise<void> {
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      const response = await action();
      if (!response.ok) {
        throw new Error(await responseError(response));
      }
      setStatus(success);
      await load();
      props.onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Mutation failed.");
    } finally {
      setSaving(false);
    }
  }

  if (loading && !policy) {
    return (
      <section className="settings-surface" data-testid="settings-surface" aria-busy="true">
        <p className="section-label">PROJECTS &amp; POLICY</p>
        <h1>Loading workspace controls…</h1>
      </section>
    );
  }

  return (
    <section className="settings-surface" data-testid="settings-surface">
      <div className="settings-heading">
        <div>
          <p className="section-label">PROJECTS &amp; POLICY</p>
          <h1>Workspace controls</h1>
          <p>Explicit configuration, versioned changes, and no mystery admin switches.</p>
        </div>
        <span className="owner-only">Owner only</span>
      </div>

      {error ? (
        <p className="inline-error" role="alert" data-testid="settings-error">
          {error}
        </p>
      ) : null}
      {status ? (
        <p className="inline-status" role="status">
          {status}
        </p>
      ) : null}

      <div className="settings-grid">
        <section className="settings-panel" aria-labelledby="projects-heading">
          <div className="panel-title-row">
            <div>
              <p className="section-label">PROJECT REGISTRY</p>
              <h2 id="projects-heading">Projects</h2>
            </div>
            <span>{projects.length}</span>
          </div>
          <ul className="admin-list">
            {projects.map((project) => (
              <li key={project.id}>
                <span className="project-swatch" style={{ backgroundColor: project.tint }} />
                <div>
                  <strong>{project.name}</strong>
                  <span>{`${project.slug} · ${project.access_mode} · v${project.resource_version}`}</span>
                  <code>{`${project.repository_host}/${project.hosted_repository_id}`}</code>
                </div>
              </li>
            ))}
          </ul>
          <form
            className="stacked-form admin-form"
            data-testid="create-project-form"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              void mutate(
                () =>
                  fetchFn(`/api/v1/workspaces/${props.workspaceId}/projects`, {
                    method: "POST",
                    headers: {
                      "content-type": "application/json",
                      "x-bfb-csrf": props.csrfToken,
                    },
                    body: JSON.stringify({
                      name: form.get("name"),
                      slug: form.get("slug"),
                      tint: String(form.get("tint") ?? "").toUpperCase(),
                      access_mode: "restricted",
                      repository_host: form.get("repository_host"),
                      hosted_repository_id: form.get("hosted_repository_id"),
                      repository_subpath: ".",
                      request_id: requestId("web-project"),
                    }),
                  }),
                "Restricted project created.",
              );
            }}
          >
            <h3>Add restricted project</h3>
            <label>
              Name
              <input name="name" required maxLength={128} />
            </label>
            <label>
              Slug
              <input name="slug" required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" />
            </label>
            <label>
              Project color
              <input name="tint" type="color" defaultValue="#6D28D9" required />
            </label>
            <label>
              Repository host
              <input name="repository_host" defaultValue="github.com" required />
            </label>
            <label>
              Hosted repository ID
              <input name="hosted_repository_id" placeholder="GitHub repository node ID" required />
            </label>
            <button type="submit" className="button-secondary" disabled={saving}>
              Add project
            </button>
          </form>
        </section>

        <section className="settings-panel" aria-labelledby="profiles-heading">
          <div className="panel-title-row">
            <div>
              <p className="section-label">AGENT PROFILES</p>
              <h2 id="profiles-heading">Profiles</h2>
            </div>
            <span>{profiles.length}</span>
          </div>
          <ul className="admin-list">
            {profiles.map((profile) => (
              <li key={profile.id}>
                <div>
                  <strong>{profile.name}</strong>
                  <span>{`${profile.provider} · ${profile.execution_mode} · ${profile.harness_mode} · ${profile.permission_mode === "autonomous" ? "Autonomous — full local-user access" : "Manual permissions"}`}</span>
                  <code>{profile.model ?? "Provider default model"}</code>
                </div>
              </li>
            ))}
          </ul>
          <form
            className="stacked-form admin-form"
            data-testid="create-profile-form"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              void mutate(
                () =>
                  fetchFn(`/api/v1/workspaces/${props.workspaceId}/agent-profiles`, {
                    method: "POST",
                    headers: {
                      "content-type": "application/json",
                      "x-bfb-csrf": props.csrfToken,
                    },
                    body: JSON.stringify({
                      name: form.get("name"),
                      provider: form.get("provider"),
                      model: String(form.get("model") ?? "") || undefined,
                      execution_mode: form.get("execution_mode"),
                      harness_mode: form.get("harness_mode"),
                      permission_mode: form.get("permission_mode"),
                      request_id: requestId("web-profile"),
                    }),
                  }),
                "Agent profile created.",
              );
            }}
          >
            <h3>Add agent profile</h3>
            <label>
              Name
              <input name="name" required maxLength={128} />
            </label>
            <label>
              Provider
              <select name="provider" defaultValue="codex">
                <option value="claude">Claude</option>
                <option value="codex">Codex</option>
                <option value="grok">Grok</option>
              </select>
            </label>
            <label>
              Model override
              <input name="model" placeholder="Provider default" />
            </label>
            <label>
              Execution mode
              <select name="execution_mode" defaultValue="interactive">
                <option value="interactive">Interactive</option>
                <option value="headless">Headless</option>
              </select>
            </label>
            <label>
              Harness mode
              <select name="harness_mode" defaultValue="standard">
                <option value="standard">Standard</option>
                <option value="restricted">Restricted</option>
              </select>
            </label>
            <label>
              Permission mode
              <select
                name="permission_mode"
                defaultValue="manual"
                aria-describedby="profile-permission-help"
              >
                <option value="manual">Manual permissions</option>
                <option value="autonomous">Autonomous — full local-user access</option>
              </select>
            </label>
            <p id="profile-permission-help">
              Autonomous mode is available only for interactive, standard Claude profiles on a
              supported runner. It skips provider permission prompts and can access files and tools
              available to the local OS user, subject to OS and provider-managed restrictions. It is
              not a workspace sandbox and does not grant deployment approval or BFB administration.
            </p>
            <button type="submit" className="button-secondary" disabled={saving}>
              Add profile
            </button>
          </form>
        </section>

        {policy ? (
          <section className="settings-panel policy-panel" aria-labelledby="policy-heading">
            <div className="panel-title-row">
              <div>
                <p className="section-label">ACTION-BOUND</p>
                <h2 id="policy-heading">Workspace policy</h2>
              </div>
              <span>{`v${policy.resourceVersion}`}</span>
            </div>
            <form
              className="stacked-form"
              data-testid="workspace-policy-form"
              onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                const allowedProviders = providerOptions.filter(
                  (provider) => form.get(provider) === "on",
                );
                const offlineTools = OFFLINE_AGENT_TOOLS.filter(
                  ([tool]) => form.get(tool) === "on",
                ).map(([tool]) => tool);
                const settings = {
                  allowed_providers: allowedProviders,
                  allow_agent_root_propose: form.get("allow_agent_root_propose") === "on",
                  allow_pass_to_agent: form.get("allow_pass_to_agent") === "on",
                  allow_run_overrides: form.get("allow_run_overrides") === "on",
                  offline_agent_work: {
                    allowed_tools: offlineTools,
                    max_pending_age_seconds: offlineTools.length
                      ? Number(form.get("offline_pending_age"))
                      : 0,
                  },
                  offline_agent_results: {
                    allow_submit_result: form.get("allow_offline_result") === "on",
                    max_pending_age_seconds:
                      form.get("allow_offline_result") === "on"
                        ? Number(form.get("offline_result_pending_age"))
                        : 0,
                  },
                };
                void mutate(async () => {
                  const targetId = await workspacePolicyStepUpTarget(
                    props.workspaceId,
                    policy.resourceVersion,
                    settings,
                  );
                  const proofId = await requestStepUpProof(fetchFn, props.csrfToken, {
                    action: "workspace.policy.update",
                    workspaceId: props.workspaceId,
                    targetId,
                    scopes: [],
                    authorizationEpoch: props.authorizationEpoch,
                  });
                  return fetchFn(`/api/v1/workspaces/${props.workspaceId}/workspace-policy`, {
                    method: "PUT",
                    headers: {
                      "content-type": "application/json",
                      "x-bfb-csrf": props.csrfToken,
                    },
                    body: JSON.stringify({
                      expected_version: policy.resourceVersion,
                      ...settings,
                      step_up_proof_id: proofId,
                      request_id: requestId("web-workspace-policy"),
                    }),
                  });
                }, "Workspace policy updated after passkey verification.");
              }}
            >
              <fieldset>
                <legend>Allowed providers</legend>
                {providerOptions.map((provider) => (
                  <label className="check-row" key={provider}>
                    <input
                      type="checkbox"
                      name={provider}
                      defaultChecked={policy.allowedProviders.includes(provider)}
                    />
                    {provider}
                  </label>
                ))}
              </fieldset>
              <fieldset>
                <legend>Capabilities</legend>
                <label className="check-row">
                  <input
                    type="checkbox"
                    name="allow_agent_root_propose"
                    defaultChecked={policy.allowAgentRootPropose}
                  />
                  Agents may propose root tasks
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    name="allow_pass_to_agent"
                    defaultChecked={policy.allowPassToAgent}
                  />
                  Humans may pass work to agents
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    name="allow_run_overrides"
                    defaultChecked={policy.allowRunOverrides}
                  />
                  Run overrides allowed
                </label>
              </fieldset>
              <fieldset>
                <legend>Offline agent-work ceiling</legend>
                {OFFLINE_AGENT_TOOLS.map(([tool, label]) => (
                  <label className="check-row" key={tool}>
                    <input
                      type="checkbox"
                      name={tool}
                      defaultChecked={policy.offlineAgentWork.allowed_tools.includes(tool)}
                    />
                    {label}
                  </label>
                ))}
                <label>
                  Maximum pending age (seconds)
                  <input
                    type="number"
                    name="offline_pending_age"
                    min="1"
                    max="300"
                    step="1"
                    required
                    defaultValue={policy.offlineAgentWork.max_pending_age_seconds || 300}
                  />
                </label>
                <p className="section-help">
                  Denied when no tools are selected. Projects and repository versions must
                  explicitly allow a subset; this ceiling alone does not permit capture or replay.
                </p>
              </fieldset>
              <fieldset>
                <legend>Offline result-submission ceiling</legend>
                <label className="check-row">
                  <input
                    type="checkbox"
                    name="allow_offline_result"
                    defaultChecked={policy.offlineAgentResults.allow_submit_result}
                  />
                  Allow pending result submissions
                </label>
                <label>
                  Maximum pending result age (seconds)
                  <input
                    type="number"
                    name="offline_result_pending_age"
                    min="1"
                    max="300"
                    step="1"
                    required
                    defaultValue={policy.offlineAgentResults.max_pending_age_seconds || 300}
                  />
                </label>
                <p className="section-help">
                  Separate from task updates. Projects and repository versions must also explicitly
                  allow results. Pending means stored locally, not submitted for review.
                </p>
              </fieldset>
              <p className="section-help">
                Saving invokes a user-verifying passkey assertion bound to this exact policy version
                and value set.
              </p>
              <button
                type="submit"
                className="button-attention"
                data-testid="save-workspace-policy"
                disabled={saving}
              >
                Verify passkey &amp; save
              </button>
            </form>
          </section>
        ) : null}
      </div>
    </section>
  );
}
